/* src/components/customGame/player/CustomMatchButton.js */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Example icons - choose appropriate ones for different modalities if desired
import { faVolumeUp, faMapMarkerAlt, faPalette, faShapes, faFont } from '@fortawesome/free-solid-svg-icons';

// Helper to get an icon based on stimulus key - can be expanded
const getButtonIcon = (stimulusKey) => {
    switch (stimulusKey?.toLowerCase()) {
        case 'audio': return faVolumeUp;
        case 'position': return faMapMarkerAlt;
        case 'color': return faPalette;
        case 'shape': return faShapes;
        case 'symbolic': return faFont;
        default: return null; // Or a generic match icon
    }
};

const CustomMatchButton = ({
    stimulusKey, // e.g., "audio", "position"
    label,       // e.g., "Audio Match"
    onClick,
    hotkey,      // e.g., "L", "A"
    disabled = false,
    // Props for feedback styling (Phase 2)
    feedbackState = null, // null, 'correct', 'incorrect', 'acknowledged'
    feedbackDuration = 300, // ms
}) => {
    const [isActiveFeedback, setIsActiveFeedback] = useState(false);

    useEffect(() => {
        if (feedbackState) {
            setIsActiveFeedback(true);
            const timer = setTimeout(() => {
                setIsActiveFeedback(false);
            }, feedbackDuration);
            return () => clearTimeout(timer);
        }
    }, [feedbackState, feedbackDuration]); // Rerun if feedbackState changes

    const baseClasses = "min-w-[120px] sm:min-w-[140px] px-4 py-3 rounded-lg shadow-md text-white font-semibold text-sm sm:text-base transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-700 flex flex-col items-center justify-center space-y-1";
    
    let buttonColorClasses = "bg-indigo-500 hover:bg-indigo-600 focus:ring-indigo-400";
    if (disabled) {
        buttonColorClasses = "bg-gray-500 cursor-not-allowed opacity-70";
    } else if (isActiveFeedback) {
        if (feedbackState === 'correct') {
            buttonColorClasses = "bg-green-500 hover:bg-green-600 focus:ring-green-400 ring-2 ring-green-300";
        } else if (feedbackState === 'incorrect') {
            buttonColorClasses = "bg-red-500 hover:bg-red-600 focus:ring-red-400 ring-2 ring-red-300";
        } else if (feedbackState === 'acknowledged') {
            buttonColorClasses = "bg-blue-500 hover:bg-blue-600 focus:ring-blue-400 ring-2 ring-blue-300 scale-105"; // Slight pop for acknowledgment
        }
    }

    const icon = getButtonIcon(stimulusKey);

    return (
        <button
            onClick={() => !disabled && onClick(stimulusKey)}
            disabled={disabled}
            className={`${baseClasses} ${buttonColorClasses}`}
            title={`${label}${hotkey ? ` (Hotkey: ${hotkey})` : ''}`}
            type="button" // Explicitly type as button
        >
            {icon && <FontAwesomeIcon icon={icon} className="h-5 w-5 mb-0.5" />}
            <span>{label}</span>
            {hotkey && !icon && ( // Show hotkey more prominently if no icon
                <span className="text-xs text-indigo-200 opacity-80">({hotkey})</span>
            )}
             {hotkey && icon && ( // Show hotkey smaller if icon is present
                <span className="text-[10px] text-indigo-200 opacity-70 leading-tight">({hotkey})</span>
            )}
        </button>
    );
};

CustomMatchButton.propTypes = {
    stimulusKey: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    hotkey: PropTypes.string,
    disabled: PropTypes.bool,
    feedbackState: PropTypes.oneOf([null, 'correct', 'incorrect', 'acknowledged']),
    feedbackDuration: PropTypes.number,
};

export default CustomMatchButton;